import React, { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import Header from "../components/Header";
import DisclaimerBanner from "../components/DisclaimerBanner";
import MessageBubble from "../components/MessageBubble";
import SuggestedQuestions from "../components/SuggestedQuestions";
import QuotaBanner from "../components/QuotaBanner";
import SubscribeModal from "../components/SubscribeModal";
import PlanBadge, { UpgradeAnswerCTA } from "../components/PlanBadge";
import { useI18n } from "../lib/i18n";
import { publicApi } from "../lib/api";

export default function Chat() {
  const { t, locale } = useI18n();
  const [messages, setMessages] = useState(() => [
    { role: "ai", text: t("chat.welcome"), sources: [] },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [currentPlan, setCurrentPlan] = useState("free");
  const endRef = useRef(null);

  // Detect current plan synchronously from localStorage + last quota payload
  useEffect(() => {
    if (localStorage.getItem("lexhab_token")) {
      setCurrentPlan("admin");
      return;
    }
    if (quota?.is_subscribed) {
      setCurrentPlan(quota.plan || "standard");
    } else {
      setCurrentPlan("free");
    }
  }, [quota]);

  const refreshQuota = useCallback(async () => {
    try {
      // Si token admin présent → pas de quota, on court-circuite
      const adminToken = localStorage.getItem("lexhab_token");
      if (adminToken) {
        setQuota({ is_admin: true, is_subscribed: true, quota_remaining: 9999, daily_limit: 0, questions_used_today: 0 });
        return;
      }
      const { data } = await publicApi.quota();
      setQuota(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    refreshQuota();
  }, [refreshQuota]);

  useEffect(() => {
    // update welcome message on locale switch only if we have no user activity yet
    setMessages((m) =>
      m.length === 1 && m[0].role === "ai"
        ? [{ role: "ai", text: t("chat.welcome"), sources: [] }]
        : m
    );
    // eslint-disable-next-line
  }, [locale]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  const send = useCallback(
    async (question) => {
      const q = (question ?? input).trim();
      if (!q || loading) return;
      setMessages((m) => [...m, { role: "user", text: q }]);
      setInput("");
      setLoading(true);
      try {
        const { data } = await publicApi.chat({
          question: q,
          language: locale,
          user_type: "public",
        });
        setMessages((m) => [
          ...m,
          {
            role: "ai",
            text: data.answer,
            sources: data.sources || [],
            queryLogId: data.query_log_id,
          },
        ]);
      } catch (e) {
        // Quota exceeded? open subscribe modal instead of error
        if (e?.response?.status === 429 && e?.response?.data?.detail?.quota_exceeded) {
          setSubscribeOpen(true);
          refreshQuota();
          // Remove the unanswered user bubble
          setMessages((m) => m.slice(0, -1));
          setInput(q);
          return;
        }
        const msg =
          locale === "fr"
            ? "Une erreur est survenue. Merci de réessayer."
            : "An error occurred. Please try again.";
        setMessages((m) => [...m, { role: "ai", text: msg, sources: [] }]);
      } finally {
        setLoading(false);
        refreshQuota();
      }
    },
    [input, loading, locale, refreshQuota]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const scrollToSource = (n) => {
    const el = document.querySelector(`[data-testid="source-item-${n}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="chat-page">
      <Header />

      <div className="flex-1 max-w-7xl w-full mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 space-y-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent font-semibold mb-2">
                / assistant
              </div>
              <h1 className="font-display text-2xl font-semibold text-primary leading-tight">
                {t("chat.title")}
              </h1>
              <p className="text-sm text-foreground/60 mt-1">{t("chat.subtitle")}</p>
            </div>
            <SuggestedQuestions onPick={send} />
            <DisclaimerBanner text={t("chat.disclaimer")} />
          </div>
        </aside>

        {/* Chat panel */}
        <main className="lg:col-span-8 xl:col-span-9 flex flex-col">
          <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
            <PlanBadge onUpgrade={() => setSubscribeOpen(true)} />
            {currentPlan === "free" && quota && !quota.is_subscribed && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/40">
                Mode Découverte — 2 questions à vie
              </span>
            )}
          </div>
          <QuotaBanner quota={quota} onSubscribe={() => setSubscribeOpen(true)} />
          <div
            className="flex-1 bg-white/50 border border-primary/10 rounded-sm p-5 md:p-8 min-h-[60vh] max-h-[72vh] overflow-y-auto"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => {
              const isLatestAi =
                m.role === "ai" &&
                i === messages.length - 1 &&
                (m.sources?.length ?? 0) > 0;
              return (
                <div key={i}>
                  <MessageBubble
                    role={m.role}
                    text={m.text}
                    sources={m.sources}
                    onCitationClick={scrollToSource}
                    queryLogId={m.queryLogId}
                  />
                  {isLatestAi && (
                    <UpgradeAnswerCTA
                      plan={currentPlan}
                      onUpgrade={() => setSubscribeOpen(true)}
                    />
                  )}
                </div>
              );
            })}
            {loading && (
              <div
                className="flex gap-3 items-center text-foreground/60 text-sm animate-fade-in"
                data-testid="loading-indicator"
              >
                <div className="w-9 h-9 bg-primary/80 text-white rounded-sm flex items-center justify-center">
                  <Loader2 size={16} className="animate-spin" />
                </div>
                <span className="font-mono text-xs tracking-wide">{t("chat.loading")}</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={onSubmit}
            className="mt-4 flex items-center gap-2 bg-white border border-primary/20 rounded-sm p-1.5 shadow-sm focus-within:border-primary transition-colors"
            data-testid="chat-form"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              disabled={loading}
              maxLength={500}
              className="flex-1 bg-transparent px-3 py-2.5 text-[15px] outline-none placeholder:text-foreground/40"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="chat-send-btn"
            >
              <Send size={15} />
              <span className="hidden sm:inline">{t("chat.send")}</span>
            </button>
          </form>
        </main>
      </div>

      <SubscribeModal
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
      />
    </div>
  );
}
